import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

/**
 * Button — handoff EXAMPLES.tsx §3 + styles.css .cta-pill/.btn
 * primary: gradient duplo + inset highlights + drop shadow tonal
 * ink: gradient ink-soft -> ink + tinted shadow
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 font-semibold transition-all duration-fast ease-out ' +
    'disabled:pointer-events-none disabled:opacity-50 ' +
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-mint-deep focus-visible:ring-offset-2 focus-visible:ring-offset-frame',
  {
    variants: {
      variant: {
        default:
          'bg-card border border-card-stroke text-fg shadow-1 hover:bg-card-soft hover:-translate-y-px active:translate-y-0',
        primary:
          'text-[#FBF7EF] bg-[linear-gradient(160deg,#6FAE93,var(--mint-deep))] ' +
          'shadow-[inset_0_1px_0_rgba(255,255,255,0.25),inset_0_-2px_0_rgba(20,40,30,0.18),0_8px_18px_-8px_rgba(40,90,70,0.45)] ' +
          'hover:-translate-y-px hover:brightness-105 active:translate-y-0',
        ink:
          'text-on-ink bg-[linear-gradient(160deg,var(--ink-soft),var(--ink))] ' +
          'shadow-[inset_0_1px_0_rgba(255,255,255,0.08),inset_0_-2px_0_rgba(0,0,0,0.2),0_8px_18px_-8px_rgba(20,16,8,0.40)] ' +
          'hover:-translate-y-px active:translate-y-0',
        danger:
          'bg-block-soft text-block border border-block/20 hover:bg-block-soft/80 hover:-translate-y-px',
        ghost:
          'bg-transparent text-fg-muted hover:text-fg hover:bg-card-soft',
        link:
          'text-mint-deep underline-offset-4 hover:underline',
      },
      size: {
        sm: 'h-9 px-3 text-xs rounded-pill',
        default: 'h-11 px-5 text-sm rounded-pill',
        lg: 'h-[46px] px-6 text-[13px] rounded-pill',
        xl: 'h-12 px-7 text-base rounded-pill',
        icon: 'h-10 w-10 rounded-pill',
      },
    },
    defaultVariants: { variant: 'default', size: 'default' },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp className={cn(buttonVariants({ variant, size }), className)} ref={ref} {...props} />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
